import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faArrowLeft, faUsers, faPuzzlePiece, faLock, faLockOpen, faKey } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import { Panel } from '../../crm/components/ui/Panel';
import { Badge } from '../../crm/components/ui/Badge';
import { Button, IconButton } from '../../crm/components/ui/Button';
import { Sheet } from '../../crm/components/ui/Sheet';
import { EmptyState } from '../../crm/components/ui/EmptyState';

const MODULOS_LABEL = { financeiro: 'Financeiro', projetos: 'Projetos', estoque: 'Estoque', patrimonio: 'Patrimônio' };
const CARGOS = ['Admin', 'user', 'dev'];

function getCookie(nome) {
  const nomeEQ = nome + '=';
  const ca = document.cookie.split(';');
  for (let c of ca) { c = c.trim(); if (c.indexOf(nomeEQ) === 0) return c.substring(nomeEQ.length); }
  return null;
}

// ─── Permissões de um usuário (§33) ──────────────────────────────────────
function PermissoesUsuarioSheet({ tenantId, usuario, onFechar, showToast }) {
  const [permissoes, setPermissoes] = useState(null);
  const base = process.env.REACT_APP_GESTAO_API_URL || '';

  const carregar = async () => {
    const token = getCookie('info');
    const r = await fetch(`${base}/api/permissoes/tenants/${tenantId}/usuarios/${usuario.id}`, { headers: { Authority: token } }).then((res) => res.json());
    if (r.status === 'ok') setPermissoes(r.modulos);
    else showToast(r.mensagem || 'Erro ao carregar permissões', 'error');
  };
  useEffect(() => { carregar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const alternar = async (modulo, campo) => {
    const token = getCookie('info');
    const valorAtual = permissoes[modulo][campo];
    const r = await fetch(`${base}/api/permissoes/tenants/${tenantId}/usuarios/${usuario.id}/modulos/${modulo}`, {
      method: 'PUT', headers: { Authority: token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ [campo]: !valorAtual }),
    }).then((res) => res.json());
    if (r.status === 'ok') carregar();
    else showToast(r.mensagem || 'Erro ao atualizar permissão', 'error');
  };

  return (
    <Sheet open onClose={onFechar} title="Permissões" subtitle={usuario.nome}>
      {permissoes === null ? (
        <div className="crm-form-hint">Carregando...</div>
      ) : (
        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead><tr><th>Módulo</th><th>Ver</th><th>Editar</th></tr></thead>
            <tbody>
              {Object.entries(MODULOS_LABEL).map(([modulo, label]) => (
                <tr key={modulo}>
                  <td className="crm-table-primary">{label}</td>
                  <td><input type="checkbox" checked={permissoes[modulo].can_view} onChange={() => alternar(modulo, 'can_view')} /></td>
                  <td><input type="checkbox" checked={permissoes[modulo].can_edit} onChange={() => alternar(modulo, 'can_edit')} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Sheet>
  );
}

const Empresas = ({ showToast }) => {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ nome: '' });

  const [empresaSelecionada, setEmpresaSelecionada] = useState(null);
  const [usuariosEmpresa, setUsuariosEmpresa] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [userForm, setUserForm] = useState({ nome: '', email: '', cargo: 'user', senha: '', confirmar_Senha: '' });
  const [usuarioPermissoes, setUsuarioPermissoes] = useState(null);

  const [empresaModulos, setEmpresaModulos] = useState(null);
  const [tenantCrm, setTenantCrm] = useState(null);
  const [loadingModulos, setLoadingModulos] = useState(false);

  const fetchEmpresas = async () => {
    setLoading(true);
    try {
      const token = getCookie('info');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/empresas/buscar`, { headers: { Authority: token, Accept: 'application/json' } });
      const result = await response.json();
      if (result.status === 'ok') setEmpresas(result.lista);
      else showToast(result.mensagem || 'Erro ao carregar empresas', 'error');
    } catch { showToast('Erro ao carregar empresas', 'error'); }
    finally { setLoading(false); }
  };

  const fetchUsuariosEmpresa = async (empresaId) => {
    setLoadingUsuarios(true);
    try {
      const token = getCookie('info');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/usuarios/buscar`, {
        method: 'POST', headers: { Authority: token, 'Content-Type': 'application/json' }, body: JSON.stringify({ empresa_id_filtro: empresaId }),
      });
      const result = await response.json();
      if (result.status === 'ok') setUsuariosEmpresa(result.lista);
      else showToast(result.mensagem || 'Erro ao carregar usuários', 'error');
    } catch { showToast('Erro ao carregar usuários', 'error'); }
    finally { setLoadingUsuarios(false); }
  };

  const gerarSlug = (nome) => nome.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const handleCadastrar = async () => {
    if (!form.nome.trim()) { showToast('Informe o nome da empresa', 'error'); return; }
    try {
      const token = getCookie('info');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/empresas/cadastrar`, {
        method: 'POST', headers: { Authority: token, 'Content-Type': 'application/json' }, body: JSON.stringify({ nome: form.nome, slug: gerarSlug(form.nome) }),
      });
      const result = await response.json();
      if (result.status === 'ok') { showToast('Empresa criada com sucesso!', 'success'); setShowModal(false); setForm({ nome: '' }); fetchEmpresas(); }
      else showToast(result.mensagem || 'Erro ao criar empresa', 'error');
    } catch { showToast('Erro ao criar empresa', 'error'); }
  };

  const handleToggleStatus = async (empresa) => {
    const acao = empresa.status === 'ativo' ? 'inativar' : 'ativar';
    const { isConfirmed } = await Swal.fire({ title: `${acao === 'inativar' ? 'Inativar' : 'Ativar'} empresa?`, text: `Empresa: ${empresa.nome}`, icon: 'warning', showCancelButton: true, confirmButtonText: 'Confirmar', cancelButtonText: 'Cancelar' });
    if (!isConfirmed) return;
    try {
      const token = getCookie('info');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/empresas/${acao}`, { method: 'POST', headers: { Authority: token, 'Content-Type': 'application/json' }, body: JSON.stringify({ id: empresa.id }) });
      const result = await response.json();
      if (result.status === 'ok') { showToast(`Empresa ${acao === 'inativar' ? 'inativada' : 'ativada'}!`, 'success'); fetchEmpresas(); }
      else showToast(result.mensagem || 'Erro ao atualizar empresa', 'error');
    } catch { showToast('Erro ao atualizar empresa', 'error'); }
  };

  const handleToggleProduto = async (empresa, produto) => {
    const habilitar = !empresa.produtos?.[produto];
    try {
      const token = getCookie('info');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/empresas/${empresa.id}/produtos`, { method: 'PUT', headers: { Authority: token, 'Content-Type': 'application/json' }, body: JSON.stringify({ [produto]: habilitar }) });
      const result = await response.json();
      if (result.status === 'ok') { showToast(`${produto === 'cofre' ? 'Cofre' : 'CRM'} ${habilitar ? 'habilitado' : 'desabilitado'} para ${empresa.nome}`, 'success'); fetchEmpresas(); }
      else showToast(result.mensagem || 'Erro ao atualizar produto', 'error');
    } catch { showToast('Erro ao atualizar produto', 'error'); }
  };

  const fetchTenantModulos = async (empresa) => {
    setLoadingModulos(true);
    try {
      const token = getCookie('info');
      const base = process.env.REACT_APP_GESTAO_API_URL || '';
      const response = await fetch(`${base}/api/admin/tenants`, { method: 'POST', headers: { Authority: token, 'Content-Type': 'application/json' }, body: JSON.stringify({ id: empresa.id, nome: empresa.nome }) });
      const result = await response.json();
      if (result.status === 'ok') { setTenantCrm(result.tenant); setEmpresaModulos(result.tenant.modulos); }
      else showToast(result.mensagem || 'Erro ao carregar módulos', 'error');
    } catch { showToast('Erro ao carregar módulos (gestao indisponível?)', 'error'); }
    finally { setLoadingModulos(false); }
  };

  const handleToggleModulo = async (modulo) => {
    const habilitado = !empresaModulos[modulo];
    try {
      const token = getCookie('info');
      const base = process.env.REACT_APP_GESTAO_API_URL || '';
      const response = await fetch(`${base}/api/admin/tenants/${tenantCrm.id}/modulos/${modulo}`, { method: 'PUT', headers: { Authority: token, 'Content-Type': 'application/json' }, body: JSON.stringify({ habilitado }) });
      const result = await response.json();
      if (result.status === 'ok') { setTenantCrm(result.tenant); setEmpresaModulos(result.tenant.modulos); showToast(`${MODULOS_LABEL[modulo]} ${habilitado ? 'habilitado' : 'desabilitado'}`, 'success'); }
      else showToast(result.mensagem || 'Erro ao atualizar módulo', 'error');
    } catch { showToast('Erro ao atualizar módulo', 'error'); }
  };

  const handleAbrirModulos = (empresa) => { setEmpresaSelecionada({ ...empresa, __painel: 'modulos' }); fetchTenantModulos(empresa); };
  const handleAbrirUsuarios = (empresa) => { setEmpresaSelecionada({ ...empresa, __painel: 'usuarios' }); fetchUsuariosEmpresa(empresa.id); };
  const handleVoltarEmpresas = () => { setEmpresaSelecionada(null); setUsuariosEmpresa([]); setShowUserModal(false); setEmpresaModulos(null); setTenantCrm(null); };

  const handleCadastrarUsuario = async () => {
    if (!userForm.nome.trim() || !userForm.email.trim() || !userForm.senha.trim()) { showToast('Preencha todos os campos', 'error'); return; }
    if (userForm.senha !== userForm.confirmar_Senha) { showToast('As senhas não coincidem', 'error'); return; }
    try {
      const token = getCookie('info');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/usuarios/cadastrar`, {
        method: 'POST', headers: { Authority: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome: userForm.nome, email: userForm.email, cargo: userForm.cargo, senha: userForm.senha, confirmar_Senha: userForm.confirmar_Senha, empresa_id_destino: empresaSelecionada.id }),
      });
      const result = await response.json();
      if (result.status === 'ok') { showToast('Usuário criado! E-mail de ativação enviado.', 'success'); setShowUserModal(false); setUserForm({ nome: '', email: '', cargo: 'user', senha: '', confirmar_Senha: '' }); fetchUsuariosEmpresa(empresaSelecionada.id); }
      else showToast(result.mensagem || 'Erro ao criar usuário', 'error');
    } catch { showToast('Erro ao criar usuário', 'error'); }
  };

  useEffect(() => { fetchEmpresas(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Painel de módulos CRM de uma empresa ---
  if (empresaSelecionada && empresaSelecionada.__painel === 'modulos') {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-3)', marginBottom: 'var(--crm-space-4)' }}>
          <button className="crm-icon-btn" onClick={handleVoltarEmpresas}><FontAwesomeIcon icon={faArrowLeft} /></button>
          <h2 style={{ margin: 0, fontSize: 'var(--crm-text-xl)', color: 'var(--crm-text-primary)' }}>Módulos — {empresaSelecionada.nome}</h2>
        </div>
        {loadingModulos || !empresaModulos ? (
          <div className="crm-form-hint">Carregando módulos...</div>
        ) : (
          <Panel>
            <div className="crm-form-hint" style={{ marginBottom: 'var(--crm-space-3)' }}>Módulo desabilitado some do menu do CRM e a API bloqueia mesmo que a rota seja chamada direto.</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-2)' }}>
              {Object.keys(MODULOS_LABEL).map((modulo) => (
                <div key={modulo} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--crm-space-3)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)' }}>
                  <span style={{ color: 'var(--crm-text-primary)' }}>{MODULOS_LABEL[modulo]}</span>
                  <Button size="sm" variant={empresaModulos[modulo] ? 'primary' : 'outline'} onClick={() => handleToggleModulo(modulo)}>{empresaModulos[modulo] ? 'Habilitado' : 'Desabilitado'}</Button>
                </div>
              ))}
            </div>
          </Panel>
        )}
      </div>
    );
  }

  // --- Painel de usuários de uma empresa ---
  if (empresaSelecionada && empresaSelecionada.__painel === 'usuarios') {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--crm-space-4)', flexWrap: 'wrap', gap: 'var(--crm-space-2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-3)' }}>
            <button className="crm-icon-btn" onClick={handleVoltarEmpresas}><FontAwesomeIcon icon={faArrowLeft} /></button>
            <h2 style={{ margin: 0, fontSize: 'var(--crm-text-xl)', color: 'var(--crm-text-primary)' }}>{empresaSelecionada.nome}</h2>
          </div>
          <Button variant="primary" icon={faPlus} onClick={() => setShowUserModal(true)}>Novo Usuário</Button>
        </div>

        <Panel flush>
          {loadingUsuarios ? (
            <EmptyState title="Carregando..." />
          ) : usuariosEmpresa.length === 0 ? (
            <EmptyState title="Nenhum usuário nesta empresa" />
          ) : (
            <div className="crm-table-wrap">
              <table className="crm-table">
                <thead><tr><th>Nome</th><th>E-mail</th><th>Cargo</th><th>Status</th><th /></tr></thead>
                <tbody>
                  {usuariosEmpresa.map((u) => (
                    <tr key={u.id}>
                      <td className="crm-table-primary">{u.nome}</td>
                      <td>{u.email}</td>
                      <td>{u.cargo === 'root' ? 'Admin' : u.cargo}</td>
                      <td><Badge tone={u.status === 'ativo' ? 'positive' : u.status === 'pendente' ? 'warning' : 'neutral'}>{u.status}</Badge></td>
                      <td><IconButton icon={faKey} label="Permissões" onClick={() => setUsuarioPermissoes(u)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Sheet open={showUserModal} onClose={() => setShowUserModal(false)} title="Novo Usuário" subtitle={empresaSelecionada.nome} footer={<Button variant="primary" onClick={handleCadastrarUsuario}>Criar</Button>}>
          <div className="crm-form-field"><label className="crm-form-label">Nome</label><input className="crm-input" value={userForm.nome} onChange={(e) => setUserForm({ ...userForm, nome: e.target.value })} /></div>
          <div className="crm-form-field"><label className="crm-form-label">E-mail</label><input className="crm-input" type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} /></div>
          <div className="crm-form-field">
            <label className="crm-form-label">Cargo</label>
            <select className="crm-select" value={userForm.cargo} onChange={(e) => setUserForm({ ...userForm, cargo: e.target.value })}>
              {CARGOS.map((c) => <option key={c} value={c}>{c === 'user' ? 'Usuário' : c}</option>)}
            </select>
          </div>
          <div className="crm-form-field"><label className="crm-form-label">Senha temporária</label><input className="crm-input" type="password" value={userForm.senha} onChange={(e) => setUserForm({ ...userForm, senha: e.target.value })} /></div>
          <div className="crm-form-field"><label className="crm-form-label">Confirmar senha</label><input className="crm-input" type="password" value={userForm.confirmar_Senha} onChange={(e) => setUserForm({ ...userForm, confirmar_Senha: e.target.value })} /></div>
        </Sheet>

        {usuarioPermissoes && (
          <PermissoesUsuarioSheet tenantId={empresaSelecionada.id} usuario={usuarioPermissoes} onFechar={() => setUsuarioPermissoes(null)} showToast={showToast} />
        )}
      </div>
    );
  }

  // --- Painel de empresas ---
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--crm-space-4)' }}>
        <Button variant="primary" icon={faPlus} onClick={() => setShowModal(true)}>Nova Empresa</Button>
      </div>

      <Panel flush>
        {loading ? (
          <EmptyState title="Carregando empresas..." />
        ) : empresas.length === 0 ? (
          <EmptyState title="Nenhuma empresa cadastrada" actions={<Button variant="primary" icon={faPlus} onClick={() => setShowModal(true)}>Nova Empresa</Button>} />
        ) : (
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead><tr><th>Empresa</th><th>Criado em</th><th>Produtos</th><th>Status</th><th>Ações</th></tr></thead>
              <tbody>
                {empresas.map((empresa) => (
                  <tr key={empresa.id}>
                    <td className="crm-table-primary">{empresa.nome}</td>
                    <td>{empresa.criado_em}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
                        <Button size="sm" variant="ghost" icon={empresa.produtos?.cofre ? faLockOpen : faLock} onClick={() => handleToggleProduto(empresa, 'cofre')}>Cofre</Button>
                        <Button size="sm" variant="ghost" icon={empresa.produtos?.crm ? faLockOpen : faLock} onClick={() => handleToggleProduto(empresa, 'crm')}>CRM</Button>
                      </div>
                    </td>
                    <td><Badge tone={empresa.status === 'ativo' ? 'positive' : 'neutral'}>{empresa.status}</Badge></td>
                    <td>
                      <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
                        <IconButton icon={faPuzzlePiece} label="Módulos" onClick={() => handleAbrirModulos(empresa)} />
                        <IconButton icon={faUsers} label="Usuários" onClick={() => handleAbrirUsuarios(empresa)} />
                        <Button size="sm" variant={empresa.status === 'ativo' ? 'danger' : 'outline'} onClick={() => handleToggleStatus(empresa)}>{empresa.status === 'ativo' ? 'Inativar' : 'Ativar'}</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Sheet open={showModal} onClose={() => setShowModal(false)} title="Nova Empresa" footer={<Button variant="primary" onClick={handleCadastrar}>Criar</Button>}>
        <div className="crm-form-field"><label className="crm-form-label">Nome da empresa</label><input className="crm-input" value={form.nome} onChange={(e) => setForm({ nome: e.target.value })} /></div>
      </Sheet>
    </div>
  );
};

export default Empresas;
